import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Camera, CalendarCheck2, MessageSquareText, Sparkles, Users, ArrowRight, Star, ClipboardList, Home as HomeIcon, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const HERO_IMG = "https://images.unsplash.com/photo-1613575831056-0acd5da8f085?auto=format&fit=crop&w=1400&q=80";
const PARENT_IMG = "https://images.unsplash.com/photo-1617638436040-76505e75940f?auto=format&fit=crop&w=900&q=80";
const STUDENT_IMG = "https://images.pexels.com/photos/3807755/pexels-photo-3807755.jpeg?auto=compress&w=900";
const CLEANER_IMG = "https://images.pexels.com/photos/9462206/pexels-photo-9462206.jpeg?auto=compress&w=900";

export default function Home() {
  return (
    <div className="grid-bg">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6">
          <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/10 border border-secondary/20" data-testid="hero-badge">
            <Sparkles size={12} className="mr-1.5" /> Parent-managed · Student-loved
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-primary">
            A clean dorm, every week.<br/>
            <span className="text-secondary">Peace of mind, every day.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            QuadScrub is the premium student living service designed for families who want their student
            to focus on classes — not laundry, dishes, and dust. We clean. We report. You relax.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 rounded-full px-6" data-testid="hero-book-btn">
              <Link to="/booking">Book a cleaning <ArrowRight size={16} className="ml-1.5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-6" data-testid="hero-howit-btn">
              <Link to="/how-it-works">See how it works</Link>
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-8 max-w-md">
            {[
              { n: "12k+", l: "Cleanings completed" },
              { n: "98%", l: "Parent satisfaction" },
              { n: "60+", l: "College campuses" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-2xl font-bold text-primary">{s.n}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bento right */}
        <div className="lg:col-span-5 grid grid-cols-6 gap-3 h-[520px]">
          <div className="col-span-6 row-span-2 rounded-2xl overflow-hidden ring-soft relative">
            <img src={HERO_IMG} alt="Clean apartment" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="text-xs uppercase tracking-widest opacity-80">Every visit</div>
              <div className="font-display font-semibold text-xl">Detailed report with before & after photos</div>
            </div>
          </div>
          <div className="col-span-3 rounded-2xl bg-secondary text-secondary-foreground p-5 flex flex-col justify-between">
            <Star className="opacity-90" />
            <div>
              <div className="font-display text-2xl font-bold">4.9</div>
              <div className="text-xs opacity-90">from 2,300+ parents</div>
            </div>
          </div>
          <div className="col-span-3 rounded-2xl bg-primary text-primary-foreground p-5 flex flex-col justify-between">
            <Camera />
            <div>
              <div className="font-display text-xl font-semibold leading-tight">Photo proof</div>
              <div className="text-xs opacity-80 mt-1">every cleaning</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted/40 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3">How it works</Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary">Four steps. Zero student involvement required.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { i: <Users />, t: "Create your account", d: "Add your student and their apartment, dorm, or leased bedroom." },
              { i: <CalendarCheck2 />, t: "Pick your cadence", d: "Weekly, bi-weekly, monthly — or a single move-in / move-out clean." },
              { i: <ShieldCheck />, t: "Pay securely", d: "One-time or recurring. Cancel or reschedule anytime." },
              { i: <Camera />, t: "Get a full report", d: "Photos, condition scores, and a summary delivered after every visit." },
            ].map((s, i) => (
              <Card key={i} className="bg-background border-border/60 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/5 text-primary flex items-center justify-center">{s.i}</div>
                  <div className="text-xs text-secondary font-bold">Step {i + 1}</div>
                  <div className="font-display font-semibold text-lg text-primary">{s.t}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PORTALS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-3 gap-6">
          {[
            { img: PARENT_IMG, title: "Parents", body: "Manage students, schedules, billing, and reports — all in one place.", to: "/parent", testId: "portal-parent" },
            { img: STUDENT_IMG, title: "Students", body: "View your cleanings, leave notes, share door codes. Mobile-first.", to: "/student", testId: "portal-student" },
            { img: CLEANER_IMG, title: "Cleaners", body: "Today's schedule, checklist, photo upload, and one-tap reports.", to: "/cleaner", testId: "portal-cleaner" },
          ].map((p) => (
            <Card key={p.title} className="overflow-hidden border-border/60">
              <div className="h-44 overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <CardContent className="p-6 space-y-3">
                <div className="font-display font-semibold text-xl text-primary">{p.title}</div>
                <p className="text-sm text-muted-foreground">{p.body}</p>
                <Link to={p.to} className="text-sm font-semibold text-secondary inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={p.testId}>
                  Open portal <ArrowRight size={14} />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-white/10 text-white hover:bg-white/10 border border-white/20 mb-3">Built for college life</Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Designed for the way students actually live.</h2>
            <p className="text-white/70 leading-relaxed">
              Whether your student is in a dorm, a shared apartment, or a 4-bedroom leased unit where each room has a separate
              contract — we tailor the cleaning to exactly what you've paid for.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { i: <HomeIcon size={18} />, t: "Per-bedroom plans", d: "Pay only for your child's room." },
              { i: <ClipboardList size={18} />, t: "Optional common areas", d: "Add kitchen, living, bathroom." },
              { i: <Wrench size={18} />, t: "Maintenance flagging", d: "We report damages & issues fast." },
              { i: <MessageSquareText size={18} />, t: "AI concierge", d: "Get plan suggestions any time." },
            ].map((x) => (
              <div key={x.t} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="w-9 h-9 rounded-lg bg-secondary/30 text-white flex items-center justify-center mb-3">{x.i}</div>
                <div className="font-display font-semibold">{x.t}</div>
                <div className="text-xs text-white/70 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display text-3xl sm:text-5xl font-bold text-primary leading-tight">
          Send your student back to a clean room — every week.
        </h2>
        <p className="mt-4 text-muted-foreground text-lg">Start with a one-time cleaning or set up recurring service in under 3 minutes.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 rounded-full px-8" data-testid="cta-book-btn">
            <Link to="/booking">Book a cleaning</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-8" data-testid="cta-pricing-btn">
            <Link to="/pricing">See pricing</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
